/**
 * PrimitiveFallbackRenderer — renders server-UI components by dispatching
 * on the `data.primitive` discriminated union.
 *
 * This is the fallback path for type_keys that don't have a registered
 * domain component in dispatchDomainComponent. It renders the generic
 * primitive stub (currently a debug card for all primitives).
 *
 * As domain components are built, fewer type_keys will reach this
 * fallback. Eventually it handles only unknown/new type_keys that
 * haven't been promoted to domain components yet.
 *
 * See: .docs/server-driven-ui/architectural-pivot-type-key-dispatch.md
 */
import React from 'react';

import type { ServerUiComponent } from '../types';

import {
  ActionCardRenderer,
  BannerRenderer,
  ChartRenderer,
  ClinicalChartRenderer,
  HeaderRenderer,
  ItemListRenderer,
  KeyValueGridRenderer,
  TextBlockRenderer,
  TileRowRenderer,
} from './primitives';

interface PrimitiveFallbackRendererProps {
  component: ServerUiComponent;
}

export const PrimitiveFallbackRenderer: React.FC<PrimitiveFallbackRendererProps> = ({
  component,
}) => {
  const { data } = component;

  if (!data) {
    return null;
  }

  switch (data.primitive) {
    case 'header':
      return <HeaderRenderer typeKey={component.type_key} data={data} />;
    case 'key_value_grid':
      return <KeyValueGridRenderer typeKey={component.type_key} data={data} />;
    case 'item_list':
      return <ItemListRenderer typeKey={component.type_key} data={data} />;
    case 'chart':
      return <ChartRenderer typeKey={component.type_key} data={data} />;
    case 'clinical_chart':
      return <ClinicalChartRenderer typeKey={component.type_key} data={data} />;
    case 'text_block':
      return <TextBlockRenderer typeKey={component.type_key} data={data} />;
    case 'tile_row':
      return <TileRowRenderer typeKey={component.type_key} data={data} />;
    case 'banner':
      return <BannerRenderer typeKey={component.type_key} data={data} />;
    case 'action_card':
      return <ActionCardRenderer typeKey={component.type_key} data={data} />;
    default:
      // Unhandled primitive — e.g. the bespoke `domain_payload` carrier, which is
      // always rendered by a registered domain component (so it shouldn't reach the
      // fallback). The generated container union differs by environment depending on
      // whether the BE that exposes `domain_payload` is deployed, so we degrade
      // gracefully here rather than asserting strict `never` exhaustiveness.
      console.warn(
        '[server-ui] Unhandled primitive in PrimitiveFallbackRenderer',
        (data as { primitive?: string }).primitive,
      );
      return null;
  }
};
